import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, FilePlus2, BookmarkPlus, Trash2, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fillTemplate } from "../../lib/decisionTemplates";
import { DocEditor } from "../DocEditor";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { EmpSelect } from "./LegalPickers";
import { errMsg } from "./legalUtils";
import { LegalTimerFields, EMPTY_TIMER } from "./LegalTimerFields";

const today = () => new Date().toISOString().slice(0, 10);

export const LegalNewCase = ({ meta, employees, onCreated, onReloadMeta }) => {
  const { company } = useAuth();
  const ed = useRef(null);
  const [f, setF] = useState({ template_key: "", title: "", employee_id: "", category: meta.categories[0], priority: "medium", due_days: 3 });
  const [timerOn, setTimerOn] = useState(false);
  const [timer, setTimer] = useState(EMPTY_TIMER);
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState("");
  const emp = (employees || []).find((e) => e.id === f.employee_id);
  const fill = (tp, e = emp) => ed.current?.setHTML(fillTemplate(tp.body_html || "", e, { company: company?.name || "الشركة", date: today() }));
  const pick = (tp) => {
    setTpl(tp); fill(tp);
    setF((p) => ({ ...p, template_key: tp.key, title: p.title || tp.title, category: tp.category || p.category, due_days: tp.days || p.due_days }));
  };
  const onEmp = (id) => { setF((p) => ({ ...p, employee_id: id })); if (tpl) fill(tpl, (employees || []).find((e) => e.id === id)); };
  const saveTpl = async () => {
    const title = window.prompt("اسم القالب الجديد", f.title || "قالب");
    if (!title) return;
    try { await api.legalTplCreate({ title, category: f.category, days: f.due_days, body_html: ed.current?.getHTML() || "" }); toast.success("تم حفظ القالب"); onReloadMeta(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delTpl = async (t) => { if (!window.confirm(`حذف القالب «${t.title}»؟`)) return; await api.legalTplDelete(t.id).catch(() => {}); onReloadMeta(); };
  const submit = async () => {
    if (!f.title.trim()) { toast.error("اكتب عنوان الحالة"); return; }
    setBusy("save");
    try { const r = await api.legalCreate({ ...f, due_days: Number(f.due_days) || 3, body_html: ed.current?.getHTML() || "", timer: timerOn && timer.deadline ? timer : null }); toast.success(`تم فتح الحالة ${r.number}`); onCreated(r.id); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const chip = (tp, mine) => (
    <span key={tp.key} className={`inline-flex items-center rounded-full border transition-colors ${f.template_key === tp.key ? "border-[#0f1f3d] bg-[#0f1f3d] text-white" : mine ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
      <button type="button" onClick={() => pick(tp)} data-testid={`legal-tpl-${tp.key}`} className="px-3 py-1.5 text-xs font-bold">{tp.title}</button>
      {mine && <button type="button" onClick={() => delTpl(tp)} className="pe-2 text-red-500" data-testid={`legal-tpl-del-${tp.id}`}><Trash2 className="h-3 w-3" /></button>}
    </span>
  );
  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]" data-testid="legal-new-case">
      <aside className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div><label className="text-xs font-semibold text-slate-500">عنوان الحالة</label>
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="مثال: تحقيق في تأخر تسليم الطلبات" data-testid="legal-new-title" /></div>
        <div><label className="text-xs font-semibold text-slate-500">الموظف المعني (المخالف / الطرف)</label><EmpSelect employees={employees} value={f.employee_id} onChange={onEmp} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-semibold text-slate-500">التصنيف</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="legal-new-category" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{meta.categories.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-slate-500">الأولوية</label>
            <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="legal-new-priority" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"><option value="high">عالية</option><option value="medium">متوسطة</option><option value="low">منخفضة</option></select></div>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">مهلة المرحلة (أيام)</label>
          <Input type="number" min={1} max={90} value={f.due_days} onChange={(e) => setF({ ...f, due_days: e.target.value })} data-testid="legal-new-days" /></div>
        <div className="rounded-xl border border-slate-200 p-2.5">
          <label className="flex items-center gap-2 text-xs font-bold text-slate-700"><input type="checkbox" checked={timerOn} onChange={(e) => setTimerOn(e.target.checked)} data-testid="legal-new-timer-toggle" /> مؤقت زمني متناقص + تنبيه واتساب</label>
          {timerOn && <div className="mt-2"><LegalTimerFields t={timer} onChange={setTimer} /></div>}
        </div>
        <Button onClick={submit} disabled={!!busy} className="w-full gap-1.5 bg-[#0f1f3d] hover:bg-[#1b3363]" data-testid="legal-new-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />} فتح الحالة وإسنادها لي
        </Button>
        <p className="text-[11px] leading-5 text-slate-400">ستُرقّم الحالة تلقائياً (LC-السنة-الرقم) وتبقى لديك حتى تصعّدها لمن تختار.</p>
      </aside>
      <div className="space-y-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-slate-600"><Sparkles className="h-3.5 w-3.5 text-amber-500" /> القوالب الثابتة (تعبئة تلقائية ببيانات الموظف)</p>
          <div className="flex flex-wrap gap-1.5">{meta.templates.map((t) => chip(t))}{meta.my_templates.map((t) => chip(t, true))}</div>
          <button type="button" onClick={saveTpl} data-testid="legal-save-tpl" className="mt-2 flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900"><BookmarkPlus className="h-3.5 w-3.5" /> حفظ النص الحالي كقالب جديد</button>
        </div>
        <DocEditor ref={ed} initialHTML="" header={<h3 className="mb-3 text-center font-heading text-lg font-extrabold">{f.title || "المحضر الأول"}</h3>} />
      </div>
    </div>
  );
};
